import { createFileRoute } from '@tanstack/react-router'
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  Locale,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { sv } from 'date-fns/locale'
import { CalendarCheckIcon, UsersIcon, WalletIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookingStatisticsType, useBookingStatistics } from '@/api/hooks/useBookingStatistics'
import { useDepartments } from '@/api/hooks/useDepartments'
import { useMembership } from '@/api/hooks/useMembership'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import PageLayout from '@/components/layout/page-layout'
import { BarChart, BarChartDatum } from '@/components/molecules/bar-chart'
import { KPICard } from '@/components/molecules/kpi-card'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/_authenticated/statistics')({
  component: StatisticsPage,
})

const formatKr = (value: number) => `${value.toLocaleString('sv-SE')} kr`

// Radix Select forbids an empty-string item value, so "all departments" uses a sentinel.
const ALL_VALUE = '__all__'

// ROI model: ~3 500 SEK per sick day in direct cost and lost production, and 1 in 4 preventive
// sessions is assumed to avert one sick day (source: Sakura's pitch + Skandia).
const SICK_DAY_COST_SEK = 3500
const SESSIONS_PER_AVERTED_SICK_DAY = 4

// Bucket granularity thresholds: up to ~a month is shown per day, up to ~six months per week
// (Monday-based), anything longer per month.
const DAILY_BUCKETS_MAX_DAYS = 35
const WEEKLY_BUCKETS_MAX_DAYS = 190

// Turns the sparse per-day counts into a continuous series over the filtered range (falling back
// to the data's own bounds): quiet days become zero buckets so the x-axis reads as a timeline,
// and long ranges aggregate per week/month to keep the bar count manageable.
const buildChartBuckets = (
  days: { date: string; count: number }[],
  range: { startDate?: string; endDate?: string },
  locale?: Locale,
): BarChartDatum[] => {
  const firstDay = days[0]
  const lastDay = days[days.length - 1]
  if (!firstDay || !lastDay) return []

  const start = parseISO(range.startDate ?? firstDay.date)
  const end = parseISO(range.endDate ?? lastDay.date)
  if (end < start) return []
  const spanDays = differenceInCalendarDays(end, start) + 1

  const daily = spanDays <= DAILY_BUCKETS_MAX_DAYS
  const weekly = !daily && spanDays <= WEEKLY_BUCKETS_MAX_DAYS

  const bucketStarts = daily
    ? eachDayOfInterval({ start, end })
    : weekly
      ? eachWeekOfInterval({ start, end }, { weekStartsOn: 1 })
      : eachMonthOfInterval({ start, end })
  const bucketOf = (day: Date) =>
    daily ? day : weekly ? startOfWeek(day, { weekStartsOn: 1 }) : startOfMonth(day)
  const labelFormat = daily || weekly ? 'd MMM' : 'MMM yyyy'

  const countByBucket = new Map<string, number>()
  for (const day of days) {
    const key = format(bucketOf(parseISO(day.date)), 'yyyy-MM-dd')
    countByBucket.set(key, (countByBucket.get(key) ?? 0) + day.count)
  }

  return bucketStarts.map(bucketStart => {
    const key = format(bucketStart, 'yyyy-MM-dd')
    return {
      key,
      label: format(bucketStart, labelFormat, { locale }),
      value: countByBucket.get(key) ?? 0,
    }
  })
}

const StatisticsSkeleton = () => (
  <div className="flex flex-col gap-6">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-36 w-full" />
      ))}
    </div>
    <Skeleton className="h-80 w-full" />
    <Skeleton className="h-56 w-full" />
  </div>
)

function StatisticsPage() {
  const { t, i18n } = useTranslation()
  const { isManager, isLoading: isMembershipLoading } = useMembership()
  const dateLocale = i18n.language.startsWith('sv') ? sv : undefined
  const { data: departments = [] } = useDepartments()

  // Bookings can't be organization-paid in the future, so today caps both pickers. Start date
  // defaults to unset (all time); end date defaults to today.
  const today = new Date()
  const todayValue = format(today, 'yyyy-MM-dd')

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState(todayValue)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [bookingType, setBookingType] = useState<BookingStatisticsType>('all')

  // A manager is always department-scoped: the backend only ever returns their own departments
  // (and rejects any other departmentId), so pre-select the first one instead of showing "all".
  useEffect(() => {
    if (isManager && !departmentId && departments[0]) setDepartmentId(departments[0].id)
  }, [isManager, departmentId, departments])

  const statisticsQuery = useBookingStatistics(
    {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      departmentId: departmentId ?? undefined,
      bookingType,
    },
    // Hold the fetch until the manager pre-selection above has resolved, so the page doesn't
    // fetch once unscoped and immediately again with the department applied.
    { enabled: !isMembershipLoading && (!isManager || !!departmentId) },
  )
  const statistics = statisticsQuery.data

  const estimatedRoiSek = statistics
    ? Math.round((statistics.totalBookings / SESSIONS_PER_AVERTED_SICK_DAY) * SICK_DAY_COST_SEK)
    : 0

  const hasFilters =
    !!startDate || endDate !== todayValue || bookingType !== 'all' || (!isManager && !!departmentId)

  return (
    <PageLayout title={t('navigation.statistics')}>
      <div className="flex flex-col gap-6">
        <div className="bg-layer-1 flex flex-wrap items-center gap-3 rounded-md border p-4">
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            placeholder={t('statisticsPage.startDatePlaceholder')}
            className="w-44"
            maxDate={today}
          />
          <span className="text-text-secondary">–</span>
          <DatePicker
            value={endDate}
            onChange={setEndDate}
            placeholder={t('statisticsPage.endDatePlaceholder')}
            className="w-44"
            maxDate={today}
          />
          <Select
            // A manager with a single department has nothing to choose between.
            disabled={isManager && departments.length <= 1}
            value={departmentId ?? ALL_VALUE}
            onValueChange={next => setDepartmentId(next === ALL_VALUE ? null : next)}
          >
            <SelectTrigger className="w-52">
              <SelectValue placeholder={t('statisticsPage.allDepartmentsOption')} />
            </SelectTrigger>
            <SelectContent>
              {!isManager && (
                <SelectItem value={ALL_VALUE}>
                  {t('statisticsPage.allDepartmentsOption')}
                </SelectItem>
              )}
              {departments.map(department => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={bookingType}
            onValueChange={next => setBookingType(next as BookingStatisticsType)}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('statisticsPage.bookingTypeAllOption')}</SelectItem>
              <SelectItem value="organization">
                {t('statisticsPage.bookingTypeOrganizationOption')}
              </SelectItem>
              <SelectItem value="friskvard">
                {t('statisticsPage.bookingTypeFriskvardOption')}
              </SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={() => {
                setStartDate('')
                setEndDate(todayValue)
                setBookingType('all')
                if (!isManager) setDepartmentId(null)
              }}
            >
              {t('statisticsPage.clearFiltersButton')}
            </Button>
          )}
        </div>

        <StateSwitcher
          isError={statisticsQuery.isError}
          isLoading={statisticsQuery.isLoading || !statistics}
          loadingState={<StatisticsSkeleton />}
        >
          <div className="flex flex-col gap-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <KPICard
                title={t('statisticsPage.bookingsCard')}
                value={statistics?.totalBookings ?? 0}
                icon={<CalendarCheckIcon />}
              />
              <KPICard
                title={t('statisticsPage.uniqueUsersCard')}
                value={statistics?.uniqueUsers ?? 0}
                icon={<UsersIcon />}
              />
              <KPICard
                title={t('statisticsPage.totalSpendCard')}
                value={formatKr(statistics?.totalSpentSek ?? 0)}
                icon={<WalletIcon />}
              />
            </div>

            <section className="bg-layer-1 flex flex-col gap-5 rounded-md border p-5">
              <h2 className="heading-4">{t('statisticsPage.bookingsOverTimeTitle')}</h2>
              {statistics && statistics.bookingsPerDay.length > 0 ? (
                <BarChart
                  data={buildChartBuckets(
                    statistics.bookingsPerDay,
                    { startDate: startDate || undefined, endDate: endDate || undefined },
                    dateLocale,
                  )}
                />
              ) : (
                <p className="body-2 text-text-secondary">
                  {t('statisticsPage.noBookingsInRange')}
                </p>
              )}
            </section>

            <section className="bg-layer-1 flex flex-col gap-4 rounded-md border p-5">
              <h2 className="heading-4">{t('statisticsPage.roiTitle')}</h2>
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold tabular-nums">
                    {formatKr(estimatedRoiSek)}
                  </span>
                </div>
                <span className="body-2 text-text-secondary max-w-prose">
                  {t('statisticsPage.roiValueLabel')}
                </span>
              </div>
            </section>
          </div>
        </StateSwitcher>
      </div>
    </PageLayout>
  )
}
